"use client";

import { motion, useAnimation, useReducedMotion, Variants } from "framer-motion";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export interface ZapHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface ZapProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  isHovered?: boolean;
  className?: string;
}

const ZapIcon = forwardRef<ZapHandle, ZapProps>(
  (
    {
      className,
      size = 24,
      duration = 1,
      isAnimated = true,
      isHovered = false,
    },
    ref,
  ) => {
    const controls = useAnimation();
    const reduced = useReducedMotion();
    const isControlled = useRef(false);
    const prevHovered = useRef(isHovered);

    useImperativeHandle(ref, () => {
      isControlled.current = true;
      return {
        startAnimation: () =>
          reduced ? controls.start("normal") : controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    // React to isHovered prop changes
    if (isHovered !== prevHovered.current) {
      prevHovered.current = isHovered;
      if (isHovered && isAnimated && !reduced) {
        controls.start("animate");
      } else if (!isHovered) {
        controls.start("normal");
      }
    }

    const handleEnter = useCallback(() => {
      if (!isAnimated || reduced) return;
      if (!isControlled.current) controls.start("animate");
    }, [controls, reduced, isAnimated]);

    const handleLeave = useCallback(() => {
      if (!isControlled.current) controls.start("normal");
    }, [controls]);

    const bodyVariants: Variants = {
      normal: {
        pathLength: 1,
        pathOffset: 0,
        opacity: 1,
        scale: 1,
        rotate: 0,
      },
      animate: {
        pathLength: [0, 1],
        pathOffset: [0.6, 0],
        opacity: [0.4, 1],
        scale: [0.92, 1.08, 1],
        rotate: [0, -4, 2, 0],
        transition: {
          pathLength: {
            duration: 0.45 * duration,
            ease: "easeOut",
          },
          pathOffset: {
            duration: 0.45 * duration,
            ease: "easeOut",
          },
          scale: {
            duration: 0.6 * duration,
            ease: "easeOut",
          },
          rotate: {
            duration: 0.6 * duration,
            ease: "easeOut",
          },
          opacity: {
            duration: 0.25 * duration,
            ease: "linear",
          },
        },
      },
    };

    return (
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ flexShrink: 0 }}
      >
        <motion.path
          d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"
          variants={bodyVariants}
          initial="normal"
          animate={controls}
        />
      </motion.svg>
    );
  },
);

ZapIcon.displayName = "ZapIcon";
export { ZapIcon };
